import type { Breadcrumb } from '@/types/breadcrumb';
import type { WorkspacePageProps } from '@/types';
import {
  admin_projects_path,
  admin_reports_path,
  profile_path,
  reports_path,
} from '@/rails/routes';
import ThemeSwitcher from '@/components/theme-switcher';
import { AppShell, useAppShellMobile } from '@astryxdesign/core/AppShell';
import { Badge } from '@astryxdesign/core/Badge';
import { BreadcrumbItem, Breadcrumbs } from '@astryxdesign/core/Breadcrumbs';
import { VStack } from '@astryxdesign/core/Layout';
import { SideNav, SideNavHeading, SideNavItem, SideNavSection } from '@astryxdesign/core/SideNav';
import { ClipboardCheck, Rows3, ShieldCheck, UserRound } from 'lucide-react';
import type { ComponentProps, MouseEvent } from 'react';

interface WorkspaceShellProps {
  children: React.ReactNode;
  currentPath: string;
  auth: WorkspacePageProps['auth'];
  appVersion: string;
  breadcrumbs?: Breadcrumb[];
}

function WorkspaceNavItem({ onClick, ...props }: ComponentProps<typeof SideNavItem>) {
  const { closeMobileNav, isMobile } = useAppShellMobile();

  const handleClick = (event: MouseEvent) => {
    onClick?.(event);
    if (isMobile) {
      closeMobileNav();
    }
  };

  return <SideNavItem {...props} onClick={handleClick} />;
}

function WorkspaceBreadcrumbs({ breadcrumbs }: { breadcrumbs: Breadcrumb[] }) {
  if (breadcrumbs.length === 0) {
    return null;
  }

  return (
    <Breadcrumbs variant="supporting">
      {breadcrumbs.map((crumb, index) => (
        <BreadcrumbItem
          key={`${crumb.text}-${index}`}
          href={crumb.href ?? undefined}
          isCurrent={!crumb.href}
        >
          {crumb.text}
        </BreadcrumbItem>
      ))}
    </Breadcrumbs>
  );
}

export default function WorkspaceShell({
  children,
  currentPath,
  auth,
  appVersion,
  breadcrumbs = [],
}: WorkspaceShellProps) {
  const isAdmin = auth.membership?.role === 'admin';
  const homeHref = reports_path();

  return (
    <AppShell
      variant="section"
      height="fill"
      contentPadding={6}
      sideNav={
        <SideNav
          header={
            <SideNavHeading
              heading="IORIO"
              headingHref={homeHref}
              icon={<img src="/iorio-icon.png" alt="" className="iorio-sidebar-logo size-7" />}
              headerEndContent={<Badge variant="teal" label={auth.team.name} />}
            />
          }
          footerIcons={
            <div className="flex flex-1 items-center justify-between">
              <ThemeSwitcher />
              <span className="text-muted-foreground text-[11px] tabular-nums">{appVersion}</span>
            </div>
          }
        >
          <SideNavSection title="Principal" isHeaderHidden>
            <WorkspaceNavItem
              label="Parte diario"
              href={reports_path()}
              icon={ClipboardCheck}
              selectedIcon={ClipboardCheck}
              isSelected={currentPath === reports_path()}
            />
          </SideNavSection>
          {isAdmin && (
            <SideNavSection title="Equipo">
              <WorkspaceNavItem
                label="Actividad"
                href={admin_reports_path()}
                icon={Rows3}
                selectedIcon={Rows3}
                isSelected={currentPath === admin_reports_path()}
              />
              <WorkspaceNavItem
                label="Administración"
                href={admin_projects_path()}
                icon={ShieldCheck}
                selectedIcon={ShieldCheck}
                isSelected={
                  currentPath.startsWith('/admin') && currentPath !== admin_reports_path()
                }
              />
            </SideNavSection>
          )}
          <SideNavSection title="Cuenta">
            <WorkspaceNavItem
              label="Perfil"
              href={profile_path()}
              icon={UserRound}
              selectedIcon={UserRound}
              isSelected={currentPath === profile_path()}
            />
          </SideNavSection>
        </SideNav>
      }
    >
      <VStack gap={4} className="iorio-workspace-content">
        <WorkspaceBreadcrumbs breadcrumbs={breadcrumbs} />
        {children}
      </VStack>
    </AppShell>
  );
}
